/**
 * Map Wave 1A FlightOfferViewModel (+ raw offer for display fields only)
 * → greenfield FlightOfferCard presentation contract.
 * Never attaches rawDeal to the output.
 */

import { formatFlightReviewDisplayMoney } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveFlightReviewDisplayMoney";
import { BAGGAGE_STATUS } from "../view-models/types";
import { mapResultsBaggageChipsFromVm } from "./resultsWave1a";

/**
 * @param {import("../view-models/types").FlightOfferViewModel} vm
 * @param {object|null|undefined} rawOffer — adapted FE offer; used only while mapping
 * @param {{ isCheapest?: boolean, airlineNameMap?: Map<string,string>|Record<string,string>|null }} [ctx]
 */
export function toGreenfieldResultsOffer(vm, rawOffer, ctx = {}) {
  if (!vm?.id) return null;

  const legs = Array.isArray(rawOffer?.cardLegRows) ? rawOffer.cardLegRows : [];
  const outbound = withIncludedBaggage(
    legToTimeline(legs[0], vm.segments?.[0], "הלוך"),
    includedBaggageForLeg(vm.baggage, 0)
  );
  const inbound = withIncludedBaggage(
    legToTimeline(legs[1], vm.segments?.[1], "חזור"),
    includedBaggageForLeg(vm.baggage, 1)
  );

  const airlineCode =
    str(
      legs.find((l) => l?.airlineCode)?.airlineCode ||
        rawOffer?.cardMeta?.airlineCode ||
        vm.segments?.[0]?.airlineCode
    ) || "XX";
  const nameFromMap = lookupAirlineName(airlineCode, ctx.airlineNameMap);
  const airlineName =
    nameFromMap ||
    str(rawOffer?.airlineSummary) ||
    str(rawOffer?.cardMeta?.airlineName) ||
    airlineCode;

  const display = vm.price?.display || null;
  const pax = Math.max(
    1,
    Number(rawOffer?.totalPaxCount) ||
      Number(vm.price?.passengerPriceBreakdown?.totalPaxCount) ||
      1
  );

  const perPassengerLabel = formatPerPassengerLabel(display);
  const totalLabel =
    display?.displayedTotalIls != null
      ? formatFlightReviewDisplayMoney(display.displayedTotalIls, "ILS")
      : null;

  const chipLabels = mapResultsBaggageChipsFromVm(vm.baggage).filter(
    (label) => !isLegIncludedChip(label)
  );
  const chips = chipLabels.map((label) => ({
    label,
    tone: baggageTone(label),
  }));

  const fareBadges = [];
  if (vm.fare?.charter === true) fareBadges.push("CHARTER");
  else if (vm.fare?.fareType) fareBadges.push(String(vm.fare.fareType));

  return {
    id: String(vm.id),
    airline: {
      code: airlineCode,
      name: airlineName,
      nameHe: null,
      logoText: airlineCode,
    },
    secondaryAirline: null,
    fareBadges,
    flags: {
      cheapest: ctx.isCheapest === true,
      direct: outbound?.stops === 0,
    },
    outbound,
    inbound: inbound || null,
    baggage: { chips },
    fare: {
      cabin: vm.fare?.cabin || null,
      family: vm.fare?.fareFamily || null,
    },
    price: {
      perPassengerLabel,
      perPassengerValue: display?.sortPrice ?? null,
      quantity: pax,
      totalLabel,
      totalNote: pax > 1 ? `סה״כ ל-${pax} נוסעים` : "סה״כ",
      lineLabel: null,
    },
    fareFamilies: [],
  };
}

function formatPerPassengerLabel(display) {
  if (!display) return null;
  if (display.isPrimaryCurrencyIls && display.displayedIls != null) {
    return formatFlightReviewDisplayMoney(display.displayedIls, "ILS");
  }
  if (display.displayedUsd != null) {
    return formatFlightReviewDisplayMoney(display.displayedUsd, "USD");
  }
  if (display.displayedIls != null) {
    return formatFlightReviewDisplayMoney(display.displayedIls, "ILS");
  }
  if (display.sortPrice != null) {
    return formatFlightReviewDisplayMoney(
      display.sortPrice,
      display.isPrimaryCurrencyIls ? "ILS" : "USD"
    );
  }
  return null;
}

function legToTimeline(leg, segment, fallbackTitle) {
  if (!leg && !segment) return null;
  const stops =
    leg?.isDirect === true
      ? 0
      : finite(segment?.stopCount) ??
        (leg?.stopLabelHe && /ישיר/u.test(leg.stopLabelHe) ? 0 : null) ??
        0;
  const stopsLabel =
    leg?.stopLabelHe ||
    (stops === 0 ? "ישיר" : stops === 1 ? "עצירה אחת" : `${stops} עצירות`);
  const duration =
    str(leg?.duration) ||
    (segment?.durationMinutes != null
      ? formatDurationMinutes(segment.durationMinutes)
      : null);
  const date =
    str(leg?.depDateDisplay) ||
    formatDepDate(leg?.depDate) ||
    null;

  return {
    title: str(leg?.labelHe) || fallbackTitle,
    weekdayLabel: null,
    depTime: str(leg?.departureTime || segment?.departureTime),
    depIata: str(leg?.originCode || segment?.originCode),
    depCity: null,
    arrTime: str(leg?.arrivalTime || segment?.arrivalTime),
    arrIata: str(leg?.destinationCode || segment?.destinationCode),
    arrCity: null,
    duration,
    stops: stops ?? 0,
    stopsLabel,
    date,
    flightNumber: str(leg?.flightNumber || segment?.flightNumber),
    operatedBy: null,
    cabin: null,
    aircraft: null,
    connection: null,
  };
}

function formatDurationMinutes(mins) {
  const n = Number(mins);
  if (!Number.isFinite(n) || n < 0) return null;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

function formatDepDate(raw) {
  if (!raw) return null;
  const s = String(raw);
  // LDS often "dd/MM/yyyy HH:mm" or ISO
  const m = s.match(/(\d{2}\/\d{2}\/\d{4})/);
  if (m) return m[1];
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return null;
}

/**
 * Included carry-on / checked only. Trip-level rows (no segmentIndex) apply to
 * every rendered leg. A segmentIndex limits the bag to that leg. Unknown,
 * paid, and not-included rows never become a positive included label.
 * @param {import("../view-models/types").BaggageViewModel[]|null|undefined} baggage
 * @param {number} legIndex
 */
export function includedBaggageForLeg(baggage, legIndex) {
  const rows = Array.isArray(baggage) ? baggage : [];
  let carryOnQty = 0;
  let checkedQty = 0;
  let carryOn = false;
  let checked = false;

  for (const row of rows) {
    if (!row || row.status !== BAGGAGE_STATUS.INCLUDED) continue;
    if (!rowAppliesToLeg(row, legIndex)) continue;
    if (isCarryOnRow(row)) {
      carryOn = true;
      carryOnQty = Math.max(carryOnQty, positiveQty(row.quantity));
    }
    if (isCheckedRow(row)) {
      checked = true;
      checkedQty = Math.max(checkedQty, positiveQty(row.quantity));
    }
  }

  if (!carryOn && !checked) return null;
  return {
    carryOn,
    checked,
    carryOnLabel: carryOn ? (carryOnQty > 1 ? `${carryOnQty} טרולי` : "טרולי") : null,
    checkedLabel: checked ? (checkedQty > 1 ? `${checkedQty} מזוודות` : "מזוודה") : null,
  };
}

function withIncludedBaggage(leg, includedBaggage) {
  if (!leg) return null;
  return { ...leg, includedBaggage: includedBaggage || null };
}

function rowAppliesToLeg(row, legIndex) {
  const index = row.segmentIndex;
  if (index == null || index === "") return true;
  const n = Number(index);
  if (!Number.isFinite(n)) return false;
  return n === legIndex;
}

function isCarryOnRow(row) {
  if (!hasIncludedQuantity(row)) return false;
  if (row.kind === "carryOn") return true;
  if (row.kind === "checked") return false;
  return /טרולי|כבודת יד/u.test(String(row.label || ""));
}

function isCheckedRow(row) {
  if (!hasIncludedQuantity(row)) return false;
  if (row.kind === "checked") return true;
  if (row.kind === "carryOn") return false;
  return /מזוודה/u.test(String(row.label || ""));
}

function hasIncludedQuantity(row) {
  return row.quantity == null || positiveQty(row.quantity) > 0;
}

function positiveQty(quantity) {
  const n = Number(quantity);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Labels that now render on the leg, not as one shared footer. */
function isLegIncludedChip(label) {
  const s = String(label || "").trim();
  return /^(?:טרולי(?: בלבד)?|\d+\s*מזוודות|מזוודה)$/u.test(s);
}

function baggageTone(label) {
  const s = String(label || "");
  if (/בתשלום|פרטי כבודה/u.test(s)) return "paid";
  if (/ללא/u.test(s)) return "muted";
  if (/טרולי|מזוודה|כלול/u.test(s)) return "included";
  return "info";
}

function lookupAirlineName(code, map) {
  if (!code || !map) return null;
  if (map instanceof Map) return map.get(code) || map.get(String(code).toUpperCase()) || null;
  return map[code] || map[String(code).toUpperCase()] || null;
}

function finite(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}
